import { motion } from "framer-motion";
import { Check, Crown, Lightbulb, Target, Type, CircleCheckBig } from "lucide-react";
import { RingHalf } from "./GeoShapes";

export const ChapterHead = ({ number, title, testid }) => (
  <div className="mb-14 flex items-end justify-between gap-6 border-b border-ink pb-6">
    <div>
      <span className="font-mono text-xs font-bold tracking-[0.25em] text-cyan-brand">
        [{number}]
      </span>
      <h2
        data-testid={testid}
        className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
      >
        {title}
      </h2>
    </div>
    <span className="hidden font-mono text-xs tracking-wider text-neutral-400 sm:block">
      GF® — {number}/04
    </span>
  </div>
);

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

// Arco magenta progressivo: 1/4 → 1/2 → 3/4 → anello completo (reference "Il processo")
const ProcessArc = ({ frac, className = "" }) => {
  const r = 42;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden="true">
      <circle
        cx="50" cy="50" r={r} fill="none"
        stroke="#E200C8" strokeWidth="15"
        strokeDasharray={`${c * frac} ${c}`}
        transform="rotate(-90 50 50)"
      />
    </svg>
  );
};

const CHECKLIST = ["Conoscenza progetto", "Meeting client", "Raccolta brief", "Inizio processo"];

const STEPS = [
  {
    num: "#1",
    title: "Onboarding",
    desc: "Comprendo il tuo business, il pubblico e gli obiettivi prima di toccare qualsiasi strumento di disegno.",
    frac: 0.25,
    Doodle: Lightbulb,
  },
  {
    num: "#2",
    title: "Strategy",
    desc: "Definisco la direzione visiva e sviluppo le proposte creative, condivise e discusse insieme.",
    frac: 0.5,
    Doodle: Crown,
  },
  {
    num: "#3",
    title: "Design",
    desc: "Costruisco il sistema visivo in tutta la sua interezza, applicato ai supporti richiesti con cura e dedizione.",
    frac: 0.75,
    Doodle: Type,
  },
  {
    num: "#4",
    title: "Deliverable",
    desc: "File pronti all'uso e supporto post-consegna per un'adozione fluida e autonoma.",
    frac: 1,
    Doodle: CircleCheckBig,
  },
];

export default function AboutSection() {
  return (
    <section id="chi-sono" data-testid="about-section" className="relative">
      <div className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
        <ChapterHead number="01" title="Chi sono & Metodo" testid="about-heading" />

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div {...reveal} className="lg:col-span-5">
            <p className="text-xl font-light leading-relaxed text-neutral-700 sm:text-2xl">
              “Prodotti di valore meritano{" "}
              <span className="font-semibold text-ink">identità di valore</span>.
              Trasformo realtà Food & Beverage con un'identità frammentata in{" "}
              <span className="font-semibold text-blue-brand">
                sistemi visivi coerenti e distintivi
              </span>
              .”
            </p>
            <p className="mt-8 max-w-md text-sm leading-relaxed text-neutral-500 sm:text-base">
              Sono Beppe, Food & Beverage Brand Designer con base a Bari.
              Attraverso ascolto, analisi e progettazione unisco brand identity,
              packaging, etichette e touchpoint digitali in un unico linguaggio:
              per comunicare il valore del prodotto, rafforzare la percezione
              del brand e distinguerti nel tuo mercato.
            </p>
            <div className="mt-10">
              <RingHalf className="h-12 w-12" />
            </div>
          </motion.div>

          <div className="lg:col-span-7" data-testid="about-process">
            <div className="flex flex-wrap items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2" data-testid="process-checklist">
              {CHECKLIST.map((c, i) => (
                <span key={c} className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-neutral-600">
                  <Check className="h-3.5 w-3.5 text-green-500" strokeWidth={3} />
                  {i + 1}) {c}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <h3 className="font-display text-5xl font-bold tracking-tight text-blue-brand sm:text-6xl">
                Il processo
              </h3>
              <span className="flex -rotate-2 items-center gap-2 rounded-full bg-magenta-brand px-5 py-2.5 font-display text-sm font-bold italic text-white" data-testid="process-pill">
                <Target className="h-4 w-4" />
                4 fasi. Un obiettivo.
              </span>
            </div>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-neutral-600 sm:text-base">
              Il mio lavoro non si basa esclusivamente sul risultato, ma su tutto
              il processo che porta a un risultato: un percorso fatto di analisi
              dei dati, ricerca e progettazione.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.1 }}
              className="group relative overflow-hidden rounded-2xl bg-[#EFF1FA] p-7 transition-colors duration-500 hover:bg-[#E6E9F8]"
              data-testid={`process-step-${i + 1}`}
            >
              <div className="relative flex items-start justify-between">
                <div className="relative">
                  <ProcessArc frac={s.frac} className="h-24 w-24 transition-transform duration-500 group-hover:rotate-6" />
                  <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-bold italic text-cyan-brand">
                    {s.num}
                  </span>
                </div>
                <s.Doodle className="h-9 w-9 rotate-12 text-neutral-400 transition-transform duration-500 group-hover:rotate-0 group-hover:text-ink" strokeWidth={1.5} />
              </div>
              <h4 className="mt-8 font-display text-2xl font-bold italic tracking-tight text-blue-brand">
                {s.title}
              </h4>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
